import { FileActionButton } from "../file-action/file-action-controls";
import React, { useEffect, useRef, useState, CSSProperties } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import ClipLoader from "react-spinners/ClipLoader";
import RewardsService, { ProductForReward } from "../../services/rewards/rewards.service";
import {
  ProductRewardRate,
  ProductRewardRateSearchDTO,
  CreateProductRewardRateDTO,
  UpdateProductRewardRateDTO,
} from "../../models/rewards-model";
import { exportProductRewardRatesToExcel } from "./ProductRewardRate-export";
import "./ProductRewardRate.css";

interface ProductOption {
  label: string;
  value: number;
}

// Filter type options - includes "All Types" for filter
const filterTypeOptions = [
  { label: "All Types", value: "" },
  { label: "Own", value: "OWN" },
  { label: "Referral", value: "REFERRAL" },
];

// Form type options - only actual values for add/edit
const formTypeOptions = [
  { label: "Own", value: "OWN" },
  { label: "Referral", value: "REFERRAL" },
];

const ProductRewardRateMain: React.FC = () => {
  const [rewardRates, setRewardRates] = useState<ProductRewardRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRate, setSelectedRate] = useState<ProductRewardRate | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Products for filter (all products) and for form (non-deleted only)
  const [allProducts, setAllProducts] = useState<ProductOption[]>([]);
  const [activeProducts, setActiveProducts] = useState<ProductOption[]>([]);

  // Pagination state
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);

  // Filter state
  const [filterProductId, setFilterProductId] = useState<number | null>(null);
  const [filterType, setFilterType] = useState<string>("");
  const [filterFromDate, setFilterFromDate] = useState<Date | null>(null);
  const [filterToDate, setFilterToDate] = useState<Date | null>(null);
  const [sortField, setSortField] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<string>("desc");

  // Form state
  const [formProductId, setFormProductId] = useState<number | null>(null);
  const [formType, setFormType] = useState<string>("REFERRAL");
  const [formRewardRate, setFormRewardRate] = useState<number>(0);
  const [formEffectiveFromDate, setFormEffectiveFromDate] = useState<Date | null>(null);
  const [formEffectiveToDate, setFormEffectiveToDate] = useState<Date | null>(null);

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalRates: 0,
    activeRates: 0,
    referralCount: 0,
    avgRewardRate: 0,
  });

  const toast = useRef<Toast>(null);

  useEffect(() => {
    fetchProducts();
    fetchRewardRates();
  }, []);

  useEffect(() => {
    fetchRewardRates();
  }, [pageNumber, pageSize, sortField, sortOrder]);

  const fetchProducts = async () => {
    try {
      const data: ProductForReward[] = await RewardsService.getProductsForReward();
      if (data && data.length > 0) {
        // All products for filter dropdown (includes deleted)
        const allProductOptions = data.map((product) => ({
          label: product.name,
          value: product.productID,
        }));
        setAllProducts([{ label: "All Products", value: 0 }, ...allProductOptions]);

        // Only non-deleted products for add form
        const activeProductOptions = data
          .filter((product) => !product.isDeleted)
          .map((product) => ({
            label: product.name,
            value: product.productID,
          }));
        setActiveProducts(activeProductOptions);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchRewardRates = async (paramsOverride?: Partial<ProductRewardRateSearchDTO>) => {
    setLoading(true);
    try {
      const formatLocal = (d: Date | null) => {
        if (!d) return undefined;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const searchParams: ProductRewardRateSearchDTO = {
        productId: paramsOverride?.productId !== undefined ? paramsOverride.productId : (filterProductId && filterProductId !== 0 ? filterProductId : null),
        type: paramsOverride?.type !== undefined ? paramsOverride.type : (filterType || ""),
        durationFromDate: paramsOverride?.durationFromDate !== undefined ? (paramsOverride.durationFromDate === null as unknown as string ? undefined : paramsOverride.durationFromDate) : formatLocal(filterFromDate),
        durationToDate: paramsOverride?.durationToDate !== undefined ? (paramsOverride.durationToDate === null as unknown as string ? undefined : paramsOverride.durationToDate) : formatLocal(filterToDate),
        sortBy: sortField,
        sortOrder: sortOrder,
        pageNumber: paramsOverride?.pageNumber !== undefined ? paramsOverride.pageNumber : pageNumber,
        pageSize: pageSize,
      };

      const response = await RewardsService.searchProductRewardRates(searchParams);
      setRewardRates(response.data || []);
      setTotalRecords(response.totalRecords || 0);

      // Calculate summary stats
      const data = response.data || [];
      const activeRates = data.filter((r) => {
        const today = new Date();
        const fromDate = new Date(r.effectiveFromDate);
        const toDate = r.effectiveToDate ? new Date(r.effectiveToDate) : null;
        return fromDate <= today && (!toDate || toDate >= today);
      });
      const referralCount = data.filter((r) => r.type === "REFERRAL").length;
      const avgRate = data.length > 0
        ? data.reduce((sum, r) => sum + r.rewardRatePer100Rs, 0) / data.length
        : 0;

      setSummaryStats({
        totalRates: response.totalRecords || 0,
        activeRates: activeRates.length,
        referralCount: referralCount,
        avgRewardRate: Math.round(avgRate * 100) / 100,
      });
    } catch (error) {
      console.error("Error fetching reward rates:", error);
      showMessage("Error", "Failed to fetch reward rates", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (summary: string, detail: string, severity: "success" | "error" | "info" | "warn") => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleSearch = () => {
    setPageNumber(1);
    setFirst(0);
    fetchRewardRates({ pageNumber: 1 });
  };

  const handleReset = () => {
    setFilterProductId(null);
    setFilterType("");
    setFilterFromDate(null);
    setFilterToDate(null);
    setPageNumber(1);
    setFirst(0);
    fetchRewardRates({
      productId: null,
      type: "",
      durationFromDate: null as unknown as string, // Cast to string to satisfy type but trigger the null override
      durationToDate: null as unknown as string,
      pageNumber: 1
    });
  };

  const handleExport = async () => {
    if (!rewardRates || rewardRates.length === 0) {
      showMessage("Warning", "No data to export", "warn");
      return;
    }

    setExportLoading(true);
    try {
      exportProductRewardRatesToExcel(rewardRates, allProducts);
      showMessage("Success", "Exported successfully", "success");
    } catch (error) {
      console.error("Export error:", error);
      showMessage("Error", "Failed to export", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const openAddDialog = () => {
    setIsEditMode(false);
    resetForm();
    setShowAddDialog(true);
  };

  const openEditDialog = (rate: ProductRewardRate) => {
    setIsEditMode(true);
    setSelectedRate(rate);
    setFormProductId(rate.productId);
    setFormType(rate.type);
    setFormRewardRate(rate.rewardRatePer100Rs);
    setFormEffectiveFromDate(rate.effectiveFromDate ? new Date(rate.effectiveFromDate) : null);
    setFormEffectiveToDate(rate.effectiveToDate ? new Date(rate.effectiveToDate) : null);
    setShowAddDialog(true);
  };

  const confirmDelete = (rate: ProductRewardRate) => {
    setSelectedRate(rate);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedRate) return;

    setDeleteLoading(true);
    try {
      const response = await RewardsService.deleteProductRewardRate(selectedRate.id);
      if (response.isSuccess) {
        showMessage("Success", "Reward rate deleted successfully", "success");
        setShowDeleteDialog(false);
        setSelectedRate(null);
        fetchRewardRates();
      } else {
        showMessage("Error", response.message || "Failed to delete", "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showMessage("Error", "An error occurred while deleting", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetForm = () => {
    setFormProductId(null);
    setFormType("REFERRAL");
    setFormRewardRate(0);
    setFormEffectiveFromDate(null);
    setFormEffectiveToDate(null);
    setSelectedRate(null);
  };

  const handleSubmit = async () => {
    if (!formProductId) {
      showMessage("Validation Error", "Please select a product", "error");
      return;
    }
    if (!formType) {
      showMessage("Validation Error", "Please select a reward type", "error");
      return;
    }
    if (formRewardRate <= 0) {
      showMessage("Validation Error", "Reward rate must be greater than 0", "error");
      return;
    }
    if (formRewardRate > 99) {
      showMessage("Validation Error", "Reward rate should not exceed 99", "error");
      return;
    }
    if (!formEffectiveFromDate) {
      showMessage("Validation Error", "Please select effective from date", "error");
      return;
    }

    const isDateInvalid = formEffectiveFromDate && formEffectiveToDate ? (() => {
      const fromD = new Date(formEffectiveFromDate);
      fromD.setHours(0, 0, 0, 0);
      const toD = new Date(formEffectiveToDate);
      toD.setHours(0, 0, 0, 0);
      return fromD > toD;
    })() : false;

    if (isDateInvalid) {
      return;
    }

    const formatDateForApi = (d: Date | null) => {
      if (!d) return null;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      if (isEditMode && selectedRate) {
        const updateData: UpdateProductRewardRateDTO = {
          id: selectedRate.id,
          productId: selectedRate.productId, // Use original value (not editable)
          type: selectedRate.type, // Use original value (not editable)
          rewardRatePer100Rs: formRewardRate,
          effectiveFromDate: formatDateForApi(formEffectiveFromDate) as string,
          effectiveToDate: formatDateForApi(formEffectiveToDate),
        };
        const response = await RewardsService.updateProductRewardRate(updateData);
        if (response.isSuccess) {
          showMessage("Success", "Reward rate updated successfully", "success");
          setShowAddDialog(false);
          fetchRewardRates();
        } else {
          showMessage("Error", response.message || "Failed to update", "error");
        }
      } else {
        const createData: CreateProductRewardRateDTO = {
          productId: formProductId,
          type: formType,
          rewardRatePer100Rs: formRewardRate,
          effectiveFromDate: formatDateForApi(formEffectiveFromDate) as string,
          effectiveToDate: formatDateForApi(formEffectiveToDate),
        };
        const response = await RewardsService.createProductRewardRate(createData);
        if (response.isSuccess) {
          showMessage("Success", "Reward rate created successfully", "success");
          setShowAddDialog(false);
          fetchRewardRates();
        } else {
          showMessage("Error", response.message || "Failed to create", "error");
        }
      }
    } catch (error) {
      console.error("Submit error:", error);
      showMessage("Error", "An error occurred", "error");
    }
  };

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setPageNumber(event.page + 1);
    setPageSize(event.rows);
  };

  const onSort = (e: any) => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getProductName = (productId: number) => {
    const product = allProducts.find((p) => p.value === productId);
    return product ? product.label : `Product #${productId}`;
  };

  const typeBodyTemplate = (rowData: ProductRewardRate) => {
    const typeColors: { [key: string]: { bg: string; text: string } } = {
      REFERRAL: { bg: "#e8f5e9", text: "#2e7d32" },
      OWN: { bg: "#e3f2fd", text: "#1565c0" },
    };
    const colors = typeColors[rowData.type] || { bg: "#f5f5f5", text: "#616161" };
    return (
      <span
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          padding: "4px 12px",
          borderRadius: "16px",
          fontSize: "0.85rem",
          fontWeight: 500,
        }}
      >
        {rowData.type}
      </span>
    );
  };

  const rateBodyTemplate = (rowData: ProductRewardRate) => {
    return (
      <span style={{ fontWeight: 600, color: "#2e7d32", fontSize: "1rem" }}>
        {rowData.rewardRatePer100Rs}
      </span>
    );
  };

  const toDateBodyTemplate = (rowData: ProductRewardRate) => {
    const expired = isExpired(rowData);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
        {/* <span style={{ textDecoration: expired ? "line-through" : "none", opacity: expired ? 0.6 : 1 }}> */}
        {formatDate(rowData.effectiveToDate)}
        {/* </span> */}
        {expired && (
          <i
            className="pi pi-exclamation-start"
            style={{
              color: "#dc2626",
              fontSize: "0.9rem",
              opacity: 0.8
            }}
            title="Expired"
          />
        )}
      </div>
    );
  };

  const isExpired = (rowData: ProductRewardRate) => {
    if (!rowData.effectiveToDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const toDate = new Date(rowData.effectiveToDate);
    toDate.setHours(0, 0, 0, 0);
    return toDate < today;
  };

  const rowClassName = (rowData: ProductRewardRate) => {
    return isExpired(rowData) ? 'expired-row' : '';
  };

  const actionBodyTemplate = (rowData: ProductRewardRate) => {
    return (
      <div className="flex justify-content-start">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-success"
          onClick={() => openEditDialog(rowData)}
          tooltip="Edit"
        />
      </div>
    );
  };

  const loaderStyle: CSSProperties = {
    display: "block",
    margin: "0 auto",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  const isFormDateInvalid = () => {
    if (formEffectiveFromDate && formEffectiveToDate) {
      const fromD = new Date(formEffectiveFromDate);
      fromD.setHours(0, 0, 0, 0);
      const toD = new Date(formEffectiveToDate);
      toD.setHours(0, 0, 0, 0);
      return fromD > toD;
    }
    return false;
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      {/* <Button
        label="Reset"
        icon="pi pi-times"
        onClick={resetForm}
        className="p-button-text"
        style={{ color: "#dc3545" }}
      /> */}
      <Button
        label={isEditMode ? "Update" : "Create"}
        icon="pi pi-check"
        onClick={handleSubmit}
        className="p-button-success"
        disabled={isFormDateInvalid() || formRewardRate > 99}
      />
    </div>
  );

  const deleteDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="No"
        icon="pi pi-times"
        onClick={() => setShowDeleteDialog(false)}
        className="p-button-text"
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        onClick={handleDelete}
        className="p-button-danger"
        loading={deleteLoading}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />

      {loading && <ClipLoader cssOverride={loaderStyle} size={50} color="#4caf50" loading={loading} />}

      <style>{`
        .complaint-header-card {
            background: linear-gradient(135deg, #f8fffe 0%, #e8f5e9 100%);
            border-radius: 16px;
            padding: 1.5rem 2rem;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            border: 1px solid rgba(76, 175, 80, 0.1);
            margin-bottom: 1.5rem;
        }

        .complaint-title-wrapper {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .complaint-icon-wrapper {
            width: 48px;
            height: 48px;
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }

        .complaint-icon-wrapper i {
            color: white;
            font-size: 1.5rem;
        }

        .register-complaint-btn {
            background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
            border: none !important;
            border-radius: 10px !important;
            padding: 0.65rem 1.5rem !important;
            font-weight: 600 !important;
            box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
            transition: all 0.3s ease !important;
        }

        .register-complaint-btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
        }

        .register-complaint-btn:active {
            transform: translateY(0) !important;
        }

        /* Search Container Styles */
        .complaint-search-container {
            background: #ffffff;
            border: 1px solid rgba(76, 175, 80, 0.15);
            border-radius: 12px;
            padding: 1rem !important;
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
            margin-top: 1rem;
        }

        .complaint-search-container .search-panel-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
            padding-bottom: 0.75rem;
            border-bottom: 1px solid #f0f0f0;
        }

        .complaint-search-container .search-panel-header i {
            color: #4CAF50;
            font-size: 1rem;
        }

        .complaint-search-container .search-panel-header span {
            font-weight: 600;
            color: #374151;
            font-size: 0.9rem;
        }

        /* Compact styling for single row */
        .complaint-search-container .p-dropdown,
        .complaint-search-container .p-calendar {
            height: 34px !important;
            min-height: 34px !important;
            font-size: 0.8125rem !important;
            border-radius: 8px !important;
            border-color: #e0e0e0 !important;
            width: 100% !important;
            align-items: center;
            background: #f9fafb !important;
        }

        .complaint-search-container .p-dropdown .p-dropdown-label {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.8125rem !important;
             display: flex;
            align-items: center;
        }

        .complaint-search-container .p-dropdown-trigger {
            width: 2rem !important;
            color: #6b7280 !important;
        }

        .complaint-search-container .p-calendar .p-inputtext {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.8125rem !important;
            background: transparent !important;
            border: none !important;
        }

        .complaint-search-container .p-calendar .p-button {
            width: 2rem !important;
            padding: 0 !important;
            background: transparent !important;
            border: none !important;
            color: #4CAF50 !important;
        }

        .complaint-search-container .p-button {
            height: 34px !important;
            min-height: 34px !important;
            padding: 0 0.75rem !important;
            font-size: 0.8125rem !important;
        }

        .complaint-search-container .p-button-icon-only {
            width: 34px !important;
            padding: 0 !important;
        }

        /* Green hover effects */
        .complaint-search-container .p-dropdown:hover,
        .complaint-search-container .p-calendar:hover {
            border-color: #4CAF50 !important;
        }

        .complaint-search-container .p-dropdown:focus-within,
        .complaint-search-container .p-calendar:focus-within {
            border-color: #4CAF50 !important;
            box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1) !important;
        }

        /* Dropdown Panel - Custom Green Style */
        .complaint-search-dropdown-panel.p-dropdown-panel {
            border-radius: 12px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
            border: 1px solid #f0f0f0 !important;
            padding: 0.5rem !important;
        }

        .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items {
            padding: 0 !important;
        }

        .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
            border-radius: 8px !important;
            margin: 2px 0 !important;
            padding: 0.6rem 1rem !important;
            font-size: 0.875rem !important;
            transition: all 0.2s ease !important;
        }

        .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
            background: #4CAF50 !important;
            color: #ffffff !important;
        }

        .complaint-search-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
            background: #4CAF50 !important;
            color: #ffffff !important;
            font-weight: 600 !important;
        }

        /* Calendar popup styling */
        .complaint-search-datepicker.p-datepicker {
            min-width: 250px !important;
            width: 250px !important;
            padding: 0.5rem !important;
            font-size: 0.8rem !important;
            border-radius: 12px !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
            border: 1px solid #e5e7eb !important;
        }

        .complaint-search-datepicker.p-datepicker .p-datepicker-header {
            padding: 0.25rem !important;
            margin-bottom: 0.25rem !important;
            border-top-left-radius: 12px !important;
            border-top-right-radius: 12px !important;
        }

        .complaint-search-datepicker .p-datepicker-title,
        .complaint-search-datepicker .p-datepicker-title .p-datepicker-month,
        .complaint-search-datepicker .p-datepicker-title .p-datepicker-year {
            font-size: 0.9rem !important;
            padding: 0.1rem !important;
            margin: 0 !important;
        }

        .complaint-search-datepicker table th {
            padding: 0.2rem !important;
            font-size: 0.75rem !important;
        }

        .complaint-search-datepicker table td {
            padding: 0 !important;
        }

        .complaint-search-datepicker table td > span {
            width: 1.75rem !important;
            height: 1.75rem !important;
            font-size: 0.8rem !important;
            margin: 1px auto !important;
        }

        /* Button styling */
        .complaint-search-container .search-button-primary {
            background: #4CAF50 !important;
            border: none !important;
            border-radius: 8px !important;
        }
        .complaint-search-container .search-button-primary:hover {
            background: #45a049 !important;
        }

        .complaint-search-container .search-button-secondary {
            background: transparent !important;
            border: 1px solid #4CAF50 !important;
            color: #4CAF50 !important;
            border-radius: 8px !important;
        }
        .complaint-search-container .search-button-secondary:hover {
            background: rgba(76, 175, 80, 0.05) !important;
            color: #45a049 !important;
            border-color: #45a049 !important;
        }

        /* Grid layout for single row */
        .complaint-search-container .compact-grid {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 0.5rem !important; /* Reduced gap to match reference */
            align-items: flex-end !important;
        }

        .complaint-search-container .compact-grid > div {
            flex: 0 0 auto;
        }
         
        /* Specific sizing overrides to match reference exact density */
        .type-filter { width: 120px; }
        .product-filter { width: 180px; }
        .date-filter { width: 130px; }
        .button-group { margin-left: 0; }

        .filter-label {
            display: block;
            margin-bottom: 0.25rem; /* Tighter label spacing */
            font-size: 0.75rem; /* Smaller label text */
            font-weight: 600;
            color: #6b7280;
        }

        /* Mobile & Responsive Enhancements */
        @media screen and (max-width: 960px) {
            .complaint-header-card {
                padding: 1.25rem 1rem !important;
            }
            .complaint-header-card .flex {
                flex-direction: column;
                align-items: stretch !important;
                gap: 1rem;
            }
            
            /* Center title contents on mobile */
            .complaint-title-wrapper {
                width: 100%;
            }
            
            .register-complaint-btn {
                width: 100%;
                justify-content: center;
                margin-top: 0.5rem;
            }
            
            /* Filter Section Vertical Stacking */
            .complaint-search-container .compact-grid {
                display: flex !important;
                flex-direction: column !important;
                align-items: stretch !important;
                gap: 1rem !important;
                flex-wrap: nowrap !important;
            }
            
            .complaint-search-container .compact-grid > div {
                min-width: 100% !important;
                max-width: 100% !important;
                width: 100% !important;
                margin: 0 !important;
                flex: none !important;
            }
            
            /* Override specific fixed widths */
            .type-filter, .product-filter, .date-filter {
                width: 100% !important;
            }

            /* Buttons layout on mobile */
            .button-group {
                margin-left: 0 !important;
                display: flex !important;
                gap: 0.75rem !important;
                margin-top: 0.5rem;
            }
            
            .button-group .p-button {
                flex: 1 !important; /* Equal width buttons */
                justify-content: center !important;
            }
            
            /* Dialog Improvements */
            .reward-rate-dialog {
                width: 92vw !important;
                margin: 0 auto;
            }
            
            .reward-rate-dialog .p-dialog-content {
                padding: 1rem !important;
            }
        }

        /* Dialog Close Button Highlight */
        .reward-rate-dialog .p-dialog-header-icon {
            width: 2.5rem !important;
            height: 2.5rem !important;
            border-radius: 50% !important;
            background: #f3f4f6 !important; /* Light gray background for visibility */
            color: #4b5563 !important;
            border: 1px solid #e5e7eb !important;
            transition: all 0.2s ease !important;
        }

        .reward-rate-dialog .p-dialog-header-icon:hover {
            background: #fee2e2 !important; /* Light red on hover */
            color: #ef4444 !important;
            border-color: #fecaca !important;
            transform: rotate(90deg);
        }

        /* Dialog Input Styling - Reduced Height */
        .reward-rate-dialog .p-dropdown,
        .reward-rate-dialog .p-inputtext,
        .reward-rate-dialog .p-calendar {
            height: 38px !important;
            min-height: 38px !important;
            border-radius: 8px !important;
            border-color: #d1d5db !important;
        }

        .reward-rate-dialog .p-dropdown .p-dropdown-label {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.875rem !important;
            line-height: 1.2 !important;
        }

        .reward-rate-dialog .p-dropdown .p-dropdown-trigger {
            width: 2rem !important;
        }

        .reward-rate-dialog .p-inputtext {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.875rem !important;
        }

        .reward-rate-dialog .p-calendar .p-inputtext {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.875rem !important;
        }

        .reward-rate-dialog .p-calendar .p-button {
            width: 2rem !important;
        }

        .reward-rate-dialog .p-inputnumber-input {
            padding: 0.4rem 0.75rem !important;
            font-size: 0.875rem !important;
            height: 38px !important;
        }

        .reward-rate-dialog .p-dropdown:not(.p-disabled).p-focus,
        .reward-rate-dialog .p-inputtext:focus,
        .reward-rate-dialog .p-calendar:focus-within {
            border-color: #4CAF50 !important;
            box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2) !important;
        }

        .reward-rate-dialog .p-dropdown:not(.p-disabled):hover,
        .reward-rate-dialog .p-inputtext:hover,
        .reward-rate-dialog .p-calendar:hover {
            border-color: #4CAF50 !important;
        }

        /* Expired Row Highlighting - Modern Design */
        .expired-row {
            background: linear-gradient(90deg, #fee2e2 0%, #fef2f2 100%) !important;
            opacity: 1 !important;
        }

        // .expired-row:hover {
        //     background: linear-gradient(90deg, #fee2e2 0%, #fef2f2 100%) !important;
        //     opacity: 1 !important;
        // }

        .expired-row td {
            color: #6b7280 !important;
        }
      `}</style>

      <div className="layout-content mt-3 mb-3">
        {/* Enhanced Header */}
        <div className="complaint-header-card">
          <div className="flex align-items-center justify-content-between">
            <div>
              <div className="complaint-title-wrapper">
                <div className="complaint-icon-wrapper">
                  <i className="pi pi-gift"></i>
                </div>
                <div>
                  <h1 className="text-800 font-bold text-3xl m-0" style={{ color: "#10492a" }}>
                    Awareness Reward Rate
                  </h1>
                  <p className="text-600 m-0 text-sm mt-1">
                    Configure and manage reward rates for products
                  </p>
                </div>
              </div>
            </div>
            <Button
              label="Add Reward Rate"
              icon="pi pi-plus"
              onClick={openAddDialog}
              className="register-complaint-btn"
            />
          </div>
        </div>

        {/* Filter Section (Mimicking ComplaintSummarySearch) */}
        <div className="complaint-search-container surface-card p-3 shadow-2 border-round-xl mt-3">
          <div className="search-panel-header">
            <i className="pi pi-filter-fill"></i>
            <span>Filter Reward Rates</span>
          </div>

          <div className="compact-grid">
            {/* Type Filter */}
            <div className="type-filter">
              <label className="filter-label">Type</label>
              <Dropdown
                value={filterType}
                options={filterTypeOptions}
                onChange={(e) => setFilterType(e.value)}
                placeholder="All Types"
                className="w-full"
                panelClassName="complaint-search-dropdown-panel"
              />
            </div>

            {/* Product Filter */}
            <div className="product-filter">
              <label className="filter-label">Product</label>
              <Dropdown
                value={filterProductId}
                options={allProducts}
                onChange={(e) => setFilterProductId(e.value)}
                placeholder="Select Product"
                className="w-full"
                filter
                showClear
                panelClassName="complaint-search-dropdown-panel"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              />
            </div>

            {/* From Date */}
            <div className="date-filter mr-2">
              <label className="filter-label">From</label>
              <Calendar
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.value as Date)}
                placeholder="Start Date"
                className="w-full compact-calendar"
                showIcon
                dateFormat="dd/mm/yy"
                maxDate={filterToDate || undefined}
                panelClassName="complaint-search-datepicker"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              />
            </div>

            {/* To Date */}
            <div className="date-filter mr-2">
              <label className="filter-label">To</label>
              <Calendar
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.value as Date)}
                placeholder="End Date"
                className="w-full compact-calendar"
                showIcon
                dateFormat="dd/mm/yy"
                minDate={filterFromDate || undefined}
                panelClassName="complaint-search-datepicker"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              />
            </div>

            {/* Buttons */}
            <div className="button-group flex gap-2">
              <Button
                icon="pi pi-search"
                onClick={handleSearch}
                className="search-button-primary"
                tooltip="Search"
                tooltipOptions={{ position: 'top' }}
              />
              <Button
                icon="pi pi-refresh"
                onClick={handleReset}
                className="search-button-secondary"
                tooltip="Reset"
                tooltipOptions={{ position: 'top' }}
              />
              <FileActionButton action="export"
                icon="pi pi-file-excel"
                onClick={handleExport}
                className="search-button-primary"
                tooltip="Export"
                loading={exportLoading}
                disabled={rewardRates.length === 0}
                tooltipOptions={{ position: 'top' }}
              />
            </div>
          </div>
        </div>

        {/* Data Table Section */}
        <div className="mt-4">
          <div className="card border-round-xl shadow-2 p-0 overflow-hidden">
            <DataTable
              value={rewardRates}
              emptyMessage="No reward rates found"
              stripedRows
              dataKey="id"
              responsiveLayout="scroll"
              sortField={sortField}
              sortOrder={sortOrder === "asc" ? 1 : -1}
              onSort={onSort}
              className="p-datatable-sm"
              rowClassName={rowClassName}
            >
              <Column field="id" header="ID" sortable style={{ width: "80px" }} />
              <Column field="productId" header="Product" body={(rowData) => getProductName(rowData.productId)} sortable />
              <Column field="type" header="Type" body={typeBodyTemplate} sortable />
              <Column field="rewardRatePer100Rs" header="Rate / ₹100" body={rateBodyTemplate} sortable />
              <Column field="effectiveFromDate" header="From Date" body={(rowData) => formatDate(rowData.effectiveFromDate)} sortable />
              <Column field="effectiveToDate" header="To Date" body={toDateBodyTemplate} sortable />
              <Column field="createdDate" header="Created" body={(rowData) => formatDate(rowData.createdDate)} />
              <Column header="Actions" body={actionBodyTemplate} style={{ width: "120px", textAlign: "center" }} />
            </DataTable>

            <Paginator
              first={first}
              rows={pageSize}
              totalRecords={totalRecords}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onPageChange={onPageChange}
              template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              className="border-none"

            />
          </div>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        visible={showAddDialog}
        header={isEditMode ? "Edit Reward Rate" : "Add Reward Rate"}
        className="reward-rate-dialog"
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        onHide={() => setShowAddDialog(false)}
        footer={dialogFooter}
      >
        <div className="form-field mb-2">
          <label htmlFor="product" className="block mb-1 font-semibold">
            Product <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="product"
            value={formProductId}
            options={activeProducts}
            onChange={(e) => setFormProductId(e.value)}
            placeholder="Select Product"
            className="w-full"
            filter
            disabled={isEditMode}
          />


          {/* <div className="form-field mb-2"> */}
          <label htmlFor="type" className="block mb-1 font-semibold">
            Reward Type <span className="text-red-500">*</span>
          </label>
          <Dropdown
            id="type"
            value={formType}
            options={formTypeOptions}
            onChange={(e) => setFormType(e.value)}
            placeholder="Select Type"
            className="w-full"
            disabled={isEditMode}
          />


          <div className="form-field mb-2">
            <label htmlFor="rate" className="block mb-1 font-semibold">
              Reward Rate (points per ₹100) <span className="text-red-500">*</span>
            </label>
            <InputNumber
              id="rate"
              value={formRewardRate}
              onValueChange={(e) => setFormRewardRate(e.value || 0)}
              min={0}
              className={`w-full ${formRewardRate > 99 ? 'p-invalid' : ''}`}
              placeholder="Enter reward rate"
            />
            {formRewardRate > 99 && (
              <small className="p-error block mt-1" style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                Reward rate should not exceed 99 points per ₹100
              </small>
            )}
          </div>

          <div className="form-field mb-2">
            <label htmlFor="fromDate" className="block mb-1 font-semibold">
              Effective From Date <span className="text-red-500">*</span>
            </label>
            <Calendar
              id="fromDate"
              value={formEffectiveFromDate}
              onChange={(e) => setFormEffectiveFromDate(e.value as Date)}
              placeholder="Select date"
              className="w-full"
              showIcon
              dateFormat="dd/mm/yy"
            />
          </div>

          <div className="form-field mb-2">
            <label htmlFor="toDate" className="block mb-1 font-semibold">
              Effective To Date
            </label>
            <Calendar
              id="toDate"
              value={formEffectiveToDate}
              onChange={(e) => setFormEffectiveToDate(e.value as Date)}
              placeholder="Select date (optional)"
              className="w-full"
              showIcon
              dateFormat="dd/mm/yy"
              minDate={formEffectiveFromDate || undefined}
            />
            {isFormDateInvalid() && (
              <small className="p-error block mt-1" style={{ color: "#dc2626", fontSize: "0.875rem", marginTop: "0.25rem" }}>
                Effective From Date cannot be greater than Effective To Date
              </small>
            )}
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteDialog}
        header="Confirm Delete"
        className="reward-rate-dialog"
        style={{ width: "400px" }}
        breakpoints={{ "641px": "95vw" }}
        modal
        onHide={() => setShowDeleteDialog(false)}
        footer={deleteDialogFooter}
      >
        <div className="flex align-items-center gap-3">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "2rem", color: "#f59e0b" }}
          ></i>
          <div>
            <p className="m-0">
              Are you sure you want to delete the reward rate for{" "}
              <strong>{selectedRate ? getProductName(selectedRate.productId) : ""}</strong>?
            </p>
            <p className="m-0 mt-2 text-muted" style={{ fontSize: "0.875rem" }}>
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ProductRewardRateMain;
